import React, { useEffect, useState } from 'react';
import HtmlEditor from './HtmlEditor';
import ModernPageBuilder from './ModernPageBuilder';
import { bannerService, menuService, pageService, menuItemService } from '../../services/api';

const EMPTY_PAGE = { title: '', content: '', meta_title: '', meta_description: '', is_published: true, meta: null };

const slugify = (t) => t.toString().toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-');

const BRAND_SLUGS = ['food', 'home-care', 'personal-care', 'beauty', 'jv-brands'];

const isBrandPage = (slug) => BRAND_SLUGS.includes(slug);

function CMSManagement() {
  const [tab, setTab] = useState('pages');
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [editingPage, setEditingPage] = useState(null); // null = new page
  const [pages, setPages] = useState([]);
  const [banners, setBanners] = useState([]);
  const [menus, setMenus] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [form, setForm] = useState(EMPTY_PAGE);
  const [brandMeta, setBrandMeta] = useState({ subtitle: '', gradient: '', bgGradient: '', icon: '', brands: [], features: [], stats: [] });
  const [bannerForm, setBannerForm] = useState({ title: '', image: '', location: 'homepage', description: '', link: '', is_active: true });
  const [menuForm, setMenuForm] = useState({ name: '', location: 'header', description: '', is_active: true });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [navParentId, setNavParentId] = useState('');
  const [showNavDrop, setShowNavDrop] = useState(false);
  const [editorMode, setEditorMode] = useState('html'); // 'html' | 'sections'

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [pd, bd, md, nd] = await Promise.all([
        pageService.index().catch(() => ({ data: [] })),
        bannerService.index().catch(() => ({ data: [] })),
        menuService.index({ with_items: 1 }).catch(() => ({ data: [] })),
        menuItemService.index().catch(() => ({ data: [] })),
      ]);
      setPages(Array.isArray(pd) ? pd : pd?.data || []);
      setBanners(Array.isArray(bd) ? bd : bd?.data || []);
      setMenus(Array.isArray(md) ? md : md?.data || []);
      setMenuItems(Array.isArray(nd) ? nd : nd?.data || []);
    } catch (e) {
      setErr('Failed to load data.');
    }
  };

  const openNew = () => { setForm(EMPTY_PAGE); setEditingPage(null); setMsg(''); setErr(''); setView('editor'); setBrandMeta({ subtitle: '', gradient: '', bgGradient: '', icon: '', brands: [], features: [], stats: [] }); };
  const openEdit = (page) => {
    setForm({ title: page.title, content: page.content || '', meta_title: page.meta_title || '', meta_description: page.meta_description || '', is_published: page.is_published ?? true, meta: page.meta || null });
    setEditingPage(page);
    setMsg(''); setErr(''); setShowNavDrop(false); setNavParentId(''); setEditorMode('html'); setView('editor');
    const meta = page.meta || {};
    setBrandMeta({
      subtitle: meta.subtitle || '',
      gradient: meta.gradient || '',
      bgGradient: meta.bgGradient || '',
      icon: meta.icon || '',
      brands: meta.brands || [],
      features: meta.features || [],
      stats: meta.stats || [],
    });
  };
  const backToList = () => { setView('list'); setEditingPage(null); setForm(EMPTY_PAGE); };

  const savePage = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setErr(''); setMsg('');
    if (!form.title.trim()) { setErr('Page title is required.'); return; }
    
    // In sections mode, content can be empty because sections are stored separately
    if (editorMode === 'html' && !form.content.trim()) { 
      setErr('Page content is required. Add some text in the editor.'); 
      return; 
    }
    
    setSaving(true);
    try {
      const isBrand = isBrandPage(editingPage?.slug) || isBrandPage(slugify(form.title));
      const payload = { ...form };
      if (isBrand) {
        payload.meta = {
          subtitle: brandMeta.subtitle,
          gradient: brandMeta.gradient,
          bgGradient: brandMeta.bgGradient,
          icon: brandMeta.icon,
          brands: brandMeta.brands.filter(b => b.name),
          features: brandMeta.features.filter(f => f.title),
          stats: brandMeta.stats.filter(s => s.value),
        };
      }
      if (editingPage) {
        await pageService.update(editingPage.id, payload);
        setMsg('✅ Page updated successfully!');
      } else {
        await pageService.store({ ...payload, slug: slugify(form.title) });
        setMsg('✅ Page created successfully!');
        setForm(EMPTY_PAGE);
        setEditingPage(null);
      }
      loadAll();
    } catch (e2) {
      setErr(e2?.response?.data?.message || 'Failed to save page.');
    } finally { setSaving(false); }
  };

  const deletePage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page? This cannot be undone.')) return;
    await pageService.destroy(id).catch(() => {});
    loadAll();
  };

  const createBrandPages = async () => {
    setErr(''); setMsg('');
    const brandDefaults = {
      food: { title: 'Food & Beverages', subtitle: 'Delicious brands that bring taste to every home.', gradient: 'from-orange-500 to-red-500', bgGradient: 'from-orange-900 via-red-900 to-slate-900', icon: '🍽', brands: [{ name: 'Golden Harvest', desc: 'Premium staples, grains, and pulses.', icon: '🌾' }, { name: 'Fresh & Pure', desc: 'Cold-pressed oils and natural foods.', icon: '🥛' }, { name: 'Desi Atta', desc: 'Traditional stone-ground flour.', icon: '🍞' }, { name: 'Ortus Foods', desc: 'Ready-to-eat meals and snacks.', icon: '🥘' }], features: [{ icon: '🌱', title: 'Farm Direct', desc: 'Sourced directly from 500+ partner farmers.' }, { icon: '🏭', title: 'Zero Preservatives', desc: 'Natural processing with no artificial additives.' }, { icon: '📦', title: 'Fresh Packaging', desc: 'Advanced packaging locks in freshness.' }, { icon: '🏆', title: 'Quality Certified', desc: 'FSSAI certified with rigorous quality control.' }], stats: [{ value: '500+', label: 'Partner Farmers' }, { value: '50+', label: 'SKUs' }, { value: '20+', label: 'Cities' }, { value: '100%', label: 'Natural' }] },
      'home-care': { title: 'Home Care', subtitle: 'Keeping homes clean and fresh every day.', gradient: 'from-blue-500 to-cyan-500', bgGradient: 'from-blue-900 via-cyan-900 to-slate-900', icon: '🏠', brands: [{ name: 'Ortus Home', desc: 'Complete home cleaning solutions.', icon: '🧽' }, { name: 'CleanMate', desc: 'Powerful surface cleaners and disinfectants.', icon: '🧴' }, { name: 'CareMate', desc: 'Fabric care and laundry detergents.', icon: '👕' }, { name: 'Voom', desc: 'Air fresheners and home fragrance products.', icon: '🌸' }], features: [{ icon: '🛡', title: 'Germ Protection', desc: 'Kills 99.9% of germs and bacteria.' }, { icon: '🌿', title: 'Eco-Friendly', desc: 'Biodegradable formulas safe for the planet.' }, { icon: '⚡', title: 'Powerful Action', desc: 'Advanced formulations that clean effectively.' }, { icon: '🌸', title: 'Long-Lasting', desc: 'Refreshing fragrances that last for hours.' }], stats: [{ value: '30+', label: 'Products' }, { value: '99.9%', label: 'Germ Kill' }, { value: '15+', label: 'Cities' }, { value: '100%', label: 'Safe Formula' }] },
      'personal-care': { title: 'Personal Care', subtitle: 'Care that goes beyond the surface.', gradient: 'from-teal-500 to-emerald-500', bgGradient: 'from-teal-900 via-emerald-900 to-slate-900', icon: '🧴', brands: [{ name: 'Ortus Care', desc: 'Daily hygiene essentials.', icon: '🧼' }, { name: 'Think Skin', desc: 'Skincare solutions for healthy skin.', icon: '✨' }, { name: 'Prim', desc: 'Hair care products for all types.', icon: '💇' }, { name: 'Pratha', desc: 'Traditional Ayurvedic personal care.', icon: '🌿' }], features: [{ icon: '🌿', title: 'Natural Ingredients', desc: 'Enriched with natural extracts and herbs.' }, { icon: '🔬', title: 'Dermatologically Tested', desc: 'Safe for all skin types.' }, { icon: '💧', title: 'Deep Hydration', desc: 'Long-lasting moisture and nourishment.' }, { icon: '👨‍👩‍👧', title: 'Family Safe', desc: 'Gentle formulas for all ages.' }], stats: [{ value: '25+', label: 'Products' }, { value: '10+', label: 'Natural Herbs' }, { value: '20+', label: 'Cities' }, { value: '100%', label: 'Cruelty-Free' }] },
      beauty: { title: 'Opulent Edge', subtitle: 'Beauty that empowers confidence.', gradient: 'from-purple-500 to-pink-500', bgGradient: 'from-purple-900 via-pink-900 to-slate-900', icon: '✨', brands: [{ name: 'Ortus Beauty', desc: 'Premium cosmetics for the confident woman.', icon: '💄' }, { name: 'Kara', desc: 'Skincare-infused beauty products.', icon: '🌸' }, { name: 'TS', desc: 'Trend-setting color cosmetics.', icon: '🎨' }, { name: 'Mysst', desc: 'Luxury fragrances and body mists.', icon: '🫧' }], features: [{ icon: '💎', title: 'Premium Quality', desc: 'International-grade formulations.' }, { icon: '🎨', title: 'Vibrant Colors', desc: 'Rich, long-lasting pigments.' }, { icon: '🌸', title: 'Skin-Friendly', desc: 'Non-comedogenic and hypoallergenic.' }, { icon: '🐰', title: 'Cruelty-Free', desc: 'Never tested on animals.' }], stats: [{ value: '40+', label: 'Products' }, { value: '50+', label: 'Shades' }, { value: '15+', label: 'Cities' }, { value: '100%', label: 'Cruelty-Free' }] },
      'jv-brands': { title: 'JV Brands', subtitle: 'Strategic partnerships for global reach.', gradient: 'from-indigo-500 to-blue-500', bgGradient: 'from-indigo-900 via-blue-900 to-slate-900', icon: '🤝', brands: [{ name: 'Terra', desc: 'Organic and natural food products.', icon: '🌍' }, { name: 'Swiss Tempelle', desc: 'Premium personal care with Swiss expertise.', icon: '🏔' }, { name: 'Sensible Portions', desc: 'Healthy snacks for mindful eating.', icon: '🥗' }, { name: 'Dreamery', desc: 'Luxury dairy desserts and ice creams.', icon: '🍦' }], features: [{ icon: '🌍', title: 'Global Standards', desc: 'International quality benchmarks.' }, { icon: '🤝', title: 'Strategic Partnerships', desc: 'Collaborations with global leaders.' }, { icon: '🏭', title: 'Local Manufacturing', desc: 'Produced locally for freshness.' }, { icon: '✅', title: 'Trusted Worldwide', desc: 'Brands trusted across multiple countries.' }], stats: [{ value: '4', label: 'JV Brands' }, { value: '10+', label: 'Countries' }, { value: '30+', label: 'Products' }, { value: '100%', label: 'Global Quality' }] },
    };

    let created = 0;
    let skipped = 0;
    for (const [slug, defaults] of Object.entries(brandDefaults)) {
      const existing = pages.find(p => p.slug === slug);
      if (existing) { skipped++; continue; }
      try {
        await pageService.store({
          title: defaults.title,
          slug,
          content: `<p>Edit this content for the ${defaults.title} brand page.</p>`,
          is_published: true,
          meta: {
            subtitle: defaults.subtitle,
            gradient: defaults.gradient,
            bgGradient: defaults.bgGradient,
            icon: defaults.icon,
            brands: defaults.brands,
            features: defaults.features,
            stats: defaults.stats,
          },
        });
        created++;
      } catch (e) {
        // skip errors
      }
    }
    setMsg(`✅ Created ${created} brand page(s)${skipped ? `, ${skipped} already existed` : ''}.`);
    loadAll();
  };

  const addToNav = async () => {
    if (!editingPage) return;
    setErr(''); setMsg('');
    try {
      await menuItemService.store({ label: editingPage.title, url: `/${editingPage.slug}`, location: 'header', parent_id: navParentId ? parseInt(navParentId) : null, sort_order: 0, is_active: true, open_new_tab: false });
      setMsg(`✅ "${editingPage.title}" added to header navigation.`);
      setShowNavDrop(false);
    } catch (e3) {
      setErr(e3?.response?.data?.message || 'Failed to add to navigation.');
    }
  };

  const headerParents = menuItems.filter((i) => i.location === 'header' && !i.parent_id);
  const editorKey = editingPage ? `edit-${editingPage.id}` : 'new';

  // ─── Pages List View ──────────────────────────────────────────
  if (tab === 'pages' && view === 'list') {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-100">Pages</h2>
            <p className="text-sm text-slate-400 mt-0.5">Create and manage your website pages</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              {['pages', 'banners', 'menus'].map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm capitalize transition ${tab === t ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>{t}</button>
              ))}
            </div>
            <button onClick={createBrandPages} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition shadow">
              🏷 Create Brand Pages
            </button>
            <button onClick={openNew} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition shadow">
              ＋ New Page
            </button>
          </div>
        </div>

        {msg && <div className="mb-4 bg-red-600/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{msg}</div>}
        {err && <div className="mb-4 bg-red-600/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{err}</div>}

        {pages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-slate-700 rounded-xl text-center">
            <div className="text-5xl mb-4">📄</div>
            <p className="text-slate-300 font-medium text-lg mb-1">No pages yet</p>
            <p className="text-slate-500 text-sm mb-5">Create your first page to get started</p>
            <button onClick={openNew} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition">＋ Create First Page</button>
          </div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-2 border-b border-slate-800 bg-slate-950">
              <div className="col-span-5">Page Title</div>
              <div className="col-span-3">URL</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {pages.map((page, idx) => (
              <div key={page.id} className={`grid grid-cols-12 items-center px-4 py-3 hover:bg-slate-800/50 transition ${idx !== 0 ? 'border-t border-slate-800' : ''}`}>
                <div className="col-span-5">
                  <button onClick={() => openEdit(page)} className="text-slate-200 font-medium hover:text-red-400 transition text-left">
                    {page.title}
                  </button>
                  {isBrandPage(page.slug) && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-900/50 text-blue-400">🏷 Brand</span>
                  )}
                </div>
                <div className="col-span-3">
                  <a href={`/#/${page.slug}`} target="_blank" rel="noreferrer" className="text-xs text-red-500 hover:underline font-mono">/{page.slug} ↗</a>
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${page.is_published ? 'bg-red-900/50 text-red-400' : 'bg-yellow-900/50 text-yellow-400'}`}>
                    {page.is_published ? '● Published' : '○ Draft'}
                  </span>
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <a href={`/#/${page.slug}`} target="_blank" rel="noreferrer" title="View page" className="text-slate-400 hover:text-red-400 transition text-sm px-1">👁</a>
                  <button onClick={() => openEdit(page)} title="Edit page" className="text-slate-400 hover:text-blue-400 transition text-sm px-1">✏️</button>
                  <button onClick={() => deletePage(page.id)} title="Delete page" className="text-slate-400 hover:text-red-400 transition text-sm px-1">🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Page Editor View ──────────────────────────────────────────
  if (tab === 'pages' && view === 'editor') {
    return (
      <div>
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={backToList} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm transition">
              ← Back to Pages
            </button>
            <span className="text-slate-600">|</span>
            <h2 className="text-lg font-semibold text-slate-100">
              {editingPage ? `Editing: ${editingPage.title}` : 'Create New Page'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {editingPage && (
              <a href={`/#/${editingPage.slug}`} target="_blank" rel="noreferrer" className="text-sm text-red-400 hover:underline px-3 py-1.5 rounded border border-slate-700 hover:border-red-500 transition">
                👁 View Live
              </a>
            )}
            <button onClick={savePage} disabled={saving} className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition ${saving ? 'bg-red-700 opacity-70 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 shadow'}`}>
              {saving ? '⏳ Saving...' : (editingPage ? '💾 Update Page' : '🚀 Publish Page')}
            </button>
          </div>
        </div>

        {msg && <div className="mb-4 bg-red-600/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{msg}</div>}
        {err && <div className="mb-4 bg-red-600/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{err}</div>}

        <div className="space-y-6">
          <div className="flex gap-5">
            {/* Main editor column */}
            <div className="flex-1 min-w-0 space-y-4">
              <input
                placeholder="Page Title — e.g. Privacy Policy"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full text-2xl font-bold bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-red-500 shadow-sm"
              />
              {form.title && (
                <div className="text-xs text-slate-500 -mt-2">
                  URL will be: <span className="font-mono text-red-400">/#/{slugify(form.title)}</span>
                </div>
              )}

              {/* Mode Switcher */}
              <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-lg p-1 w-fit">
                <button
                  type="button"
                  onClick={() => setEditorMode('html')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${editorMode === 'html' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  ✏️ HTML Editor
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('sections')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${editorMode === 'sections' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  🏗 Section Builder
                </button>
              </div>
              <p className="text-[11px] text-slate-500 -mt-2">
                {editorMode === 'html'
                  ? 'Use the rich text editor for simple pages with headings, paragraphs, and images.'
                  : 'Build modern pages with drag-drop sections like Hero, Features, Stats, and CTA blocks.'}
              </p>

              {editorMode === 'html' && (
                <HtmlEditor
                  value={form.content}
                  onChange={(content) => setForm({ ...form, content })}
                  editorKey={editorKey}
                />
              )}

              {editorMode === 'sections' && (
                <div className="border border-slate-700 rounded-xl overflow-hidden">
                  <ModernPageBuilder pageSlug={editingPage ? editingPage.slug : (form.title ? slugify(form.title) : '')} />
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="w-72 flex-shrink-0 space-y-4">
              {/* Publish box */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="bg-slate-800 px-4 py-2.5 font-semibold text-sm text-slate-200">📋 Page Settings</div>
                <div className="p-4 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className={`relative w-10 h-5 rounded-full transition ${form.is_published ? 'bg-red-500' : 'bg-slate-600'}`} onClick={() => setForm({ ...form, is_published: !form.is_published })}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_published ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-sm text-slate-300">{form.is_published ? '🟢 Published' : '🟡 Draft (not visible)'}</span>
                  </label>
                  {editingPage && (
                    <div className="text-xs text-slate-500 border-t border-slate-800 pt-3">
                      <div>Slug: <span className="font-mono text-slate-300">{editingPage.slug}</span></div>
                      <div className="mt-1">ID: #{editingPage.id}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* SEO box */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="bg-slate-800 px-4 py-2.5 font-semibold text-sm text-slate-200">🔍 SEO Settings</div>
                <div className="p-4 space-y-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Meta Title</label>
                    <input placeholder="Page title for Google..." value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Meta Description</label>
                    <textarea placeholder="Brief description for search engines..." rows={3} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-red-500 resize-none" />
                  </div>
                </div>
              </div>

              {/* Brand Page Editor */}
              {(editingPage && isBrandPage(editingPage.slug)) || (!editingPage && isBrandPage(slugify(form.title))) ? (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-800 px-4 py-2.5 font-semibold text-sm text-slate-200">🏷 Brand Page Settings</div>
                  <div className="p-4 space-y-4">
                    {/* Basic brand fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Subtitle</label>
                        <input placeholder="e.g. Delicious brands..." value={brandMeta.subtitle} onChange={(e) => setBrandMeta({ ...brandMeta, subtitle: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Icon (emoji)</label>
                        <input placeholder="e.g. 🍽" value={brandMeta.icon} onChange={(e) => setBrandMeta({ ...brandMeta, icon: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Gradient</label>
                        <input placeholder="e.g. from-orange-500 to-red-500" value={brandMeta.gradient} onChange={(e) => setBrandMeta({ ...brandMeta, gradient: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">BG Gradient</label>
                        <input placeholder="e.g. from-orange-900 via-red-900 to-slate-900" value={brandMeta.bgGradient} onChange={(e) => setBrandMeta({ ...brandMeta, bgGradient: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                      </div>
                    </div>

                    {/* Brands */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs text-slate-400 font-semibold">Brands</label>
                        <button type="button" onClick={() => setBrandMeta({ ...brandMeta, brands: [...brandMeta.brands, { name: '', desc: '', icon: '' }] })} className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded transition">＋ Add Brand</button>
                      </div>
                      <div className="space-y-2">
                        {brandMeta.brands.map((brand, i) => (
                          <div key={i} className="flex gap-2 items-start">
                            <input placeholder="Icon" value={brand.icon || ''} onChange={(e) => { const b = [...brandMeta.brands]; b[i] = { ...b[i], icon: e.target.value }; setBrandMeta({ ...brandMeta, brands: b }); }} className="w-12 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <input placeholder="Brand name" value={brand.name || ''} onChange={(e) => { const b = [...brandMeta.brands]; b[i] = { ...b[i], name: e.target.value }; setBrandMeta({ ...brandMeta, brands: b }); }} className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <input placeholder="Description" value={brand.desc || ''} onChange={(e) => { const b = [...brandMeta.brands]; b[i] = { ...b[i], desc: e.target.value }; setBrandMeta({ ...brandMeta, brands: b }); }} className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <button type="button" onClick={() => setBrandMeta({ ...brandMeta, brands: brandMeta.brands.filter((_, idx) => idx !== i) })} className="text-xs text-red-400 hover:text-red-300 px-1">✕</button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Features */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs text-slate-400 font-semibold">Features</label>
                        <button type="button" onClick={() => setBrandMeta({ ...brandMeta, features: [...brandMeta.features, { icon: '', title: '', desc: '' }] })} className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded transition">＋ Add Feature</button>
                      </div>
                      <div className="space-y-2">
                        {brandMeta.features.map((feature, i) => (
                          <div key={i} className="flex gap-2 items-start">
                            <input placeholder="Icon" value={feature.icon || ''} onChange={(e) => { const f = [...brandMeta.features]; f[i] = { ...f[i], icon: e.target.value }; setBrandMeta({ ...brandMeta, features: f }); }} className="w-12 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <input placeholder="Title" value={feature.title || ''} onChange={(e) => { const f = [...brandMeta.features]; f[i] = { ...f[i], title: e.target.value }; setBrandMeta({ ...brandMeta, features: f }); }} className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <input placeholder="Description" value={feature.desc || ''} onChange={(e) => { const f = [...brandMeta.features]; f[i] = { ...f[i], desc: e.target.value }; setBrandMeta({ ...brandMeta, features: f }); }} className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <button type="button" onClick={() => setBrandMeta({ ...brandMeta, features: brandMeta.features.filter((_, idx) => idx !== i) })} className="text-xs text-red-400 hover:text-red-300 px-1">✕</button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs text-slate-400 font-semibold">Stats</label>
                        <button type="button" onClick={() => setBrandMeta({ ...brandMeta, stats: [...brandMeta.stats, { value: '', label: '' }] })} className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded transition">＋ Add Stat</button>
                      </div>
                      <div className="space-y-2">
                        {brandMeta.stats.map((stat, i) => (
                          <div key={i} className="flex gap-2 items-center">
                            <input placeholder="Value (e.g. 500+)" value={stat.value || ''} onChange={(e) => { const s = [...brandMeta.stats]; s[i] = { ...s[i], value: e.target.value }; setBrandMeta({ ...brandMeta, stats: s }); }} className="w-24 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <input placeholder="Label (e.g. Partner Farmers)" value={stat.label || ''} onChange={(e) => { const s = [...brandMeta.stats]; s[i] = { ...s[i], label: e.target.value }; setBrandMeta({ ...brandMeta, stats: s }); }} className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500" />
                            <button type="button" onClick={() => setBrandMeta({ ...brandMeta, stats: brandMeta.stats.filter((_, idx) => idx !== i) })} className="text-xs text-red-400 hover:text-red-300 px-1">✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Add to Navigation */}
              {editingPage && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-800 px-4 py-2.5 font-semibold text-sm text-slate-200">🧭 Navigation</div>
                  <div className="p-4">
                    {!showNavDrop ? (
                      <button type="button" onClick={() => setShowNavDrop(true)} className="w-full text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-lg px-3 py-2 transition">
                        ＋ Add to Header Menu
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <label className="text-xs text-slate-400">Place under:</label>
                        <select value={navParentId} onChange={(e) => setNavParentId(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none">
                          <option value="">Top-level menu item</option>
                          {headerParents.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                        <div className="flex gap-2">
                          <button type="button" onClick={addToNav} className="flex-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded px-2 py-1.5 transition">Confirm</button>
                          <button type="button" onClick={() => setShowNavDrop(false)} className="text-xs text-slate-400 hover:text-slate-200 px-2">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button 
                type="button" 
                onClick={savePage} 
                disabled={saving} 
                className={`w-full py-3 rounded-xl text-sm font-bold text-white transition ${saving ? 'bg-red-700 opacity-70 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 shadow-lg'}`}
              >
                {saving ? '⏳ Saving...' : (editingPage ? '💾 Update Page' : '🚀 Publish Page')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Banners Tab ──────────────────────────────────────────────
  if (tab === 'banners') {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-100">Banners</h2>
          <div className="flex gap-2">
            {['pages', 'banners', 'menus'].map((t) => (<button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm capitalize transition ${tab === t ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>{t}</button>))}
          </div>
        </div>
        {msg && <div className="mb-4 bg-red-600/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{msg}</div>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <form onSubmit={async (e) => { e.preventDefault(); await bannerService.store(bannerForm); setBannerForm({ title: '', image: '', location: 'homepage', description: '', link: '', is_active: true }); setMsg('Banner created.'); loadAll(); }} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-3">
            <h3 className="font-semibold text-slate-100">Create Banner</h3>
            <input required placeholder="Banner Title" value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
            <input required placeholder="Image URL" value={bannerForm.image} onChange={(e) => setBannerForm({ ...bannerForm, image: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
            <input placeholder="Location (e.g. homepage)" value={bannerForm.location} onChange={(e) => setBannerForm({ ...bannerForm, location: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
            <input placeholder="Link URL (optional)" value={bannerForm.link} onChange={(e) => setBannerForm({ ...bannerForm, link: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
            <textarea placeholder="Description (optional)" value={bannerForm.description} onChange={(e) => setBannerForm({ ...bannerForm, description: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
            <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition">Save Banner</button>
          </form>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="font-semibold text-slate-100 mb-4">Banners ({banners.length})</h3>
            {banners.map((b) => (<div key={b.id} className="flex justify-between border-b border-slate-800 py-3"><div><div className="text-slate-200 font-medium">{b.title}</div><div className="text-xs text-slate-500">{b.location}</div></div><button onClick={async () => { if (window.confirm('Delete?')) { await bannerService.destroy(b.id); loadAll(); } }} className="text-red-400 hover:text-red-300 text-sm">🗑 Delete</button></div>))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Menus Tab ────────────────────────────────────────────────
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-100">Menus</h2>
        <div className="flex gap-2">
          {['pages', 'banners', 'menus'].map((t) => (<button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm capitalize transition ${tab === t ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>{t}</button>))}
        </div>
      </div>
      {msg && <div className="mb-4 bg-red-600/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{msg}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={async (e) => { e.preventDefault(); await menuService.store(menuForm); setMenuForm({ name: '', location: 'header', description: '', is_active: true }); setMsg('Menu created.'); loadAll(); }} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-3">
          <h3 className="font-semibold text-slate-100">Create Menu</h3>
          <input required placeholder="Menu Name" value={menuForm.name} onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
          <input placeholder="Location (e.g. header)" value={menuForm.location} onChange={(e) => setMenuForm({ ...menuForm, location: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
          <textarea placeholder="Description (optional)" value={menuForm.description} onChange={(e) => setMenuForm({ ...menuForm, description: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 focus:outline-none" />
          <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition">Save Menu</button>
        </form>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h3 className="font-semibold text-slate-100 mb-4">Menus ({menus.length})</h3>
          {menus.map((m) => (<div key={m.id} className="flex justify-between border-b border-slate-800 py-3"><div><div className="text-slate-200 font-medium">{m.name}</div><div className="text-xs text-slate-500">{m.location}</div></div><button onClick={async () => { if (window.confirm('Delete?')) { await menuService.destroy(m.id); loadAll(); } }} className="text-red-400 hover:text-red-300 text-sm">🗑 Delete</button></div>))}
        </div>
      </div>
    </div>
  );
}

export default CMSManagement;
